import React, { useEffect, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import { GrClose } from "react-icons/gr";
import { FiCamera, FiUpload } from "react-icons/fi";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { ReactComponent as PDFIcon } from "../../img/icons/icon_pdf.svg";
import ImageFileIcon from "../../img/image-file-icon.png";
import HttpClient, {
  HttpGetOutput,
  HttpMutationOutput,
} from "../../lib/HttpClient";
import { useSnackbarContext } from "../../contexts/Snackbar";
import utils from "../../Utils";
import ConfirmDeleteReportDialog from "./ConfirmDeleteReportDialog";

interface UploadDialogInterface {
  closeFn: () => void;
  patientId: String;
  isFromAppointment: Boolean;
  appointmentId?: String;
  patientName?: string;
  patientAge?: string | number | null;
  patientGender?: string | null;
}

const REPORT_TYPES = [
  "Pathology",
  "Imaging",
  "Neurophysiology",
  "Genetics",
  "Psychology",
] as const;

type ReportType = (typeof REPORT_TYPES)[number];

// Imaging is stored as Bodyscan, matching the previous upload API.
const reportTypeForApi = (type: ReportType) =>
  type === "Imaging" ? "Bodyscan" : type;

const isImageFormat = (format?: string) => {
  const value = format?.toLowerCase();
  return value === "png" || value === "jpg" || value === "jpeg";
};

function FileTypeIcon({ pdf }: { pdf: boolean }) {
  if (pdf) {
    return <PDFIcon className="upload-report-dialog__file-icon" aria-hidden />;
  }
  return (
    <img
      src={ImageFileIcon}
      alt=""
      className="upload-report-dialog__file-icon"
    />
  );
}

const allowedFileTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];

const invalid_report_err_msg =
  "You can only upload photos (PNG, JPG, JPEG) or PDF files. Audio and Video files are not supported. Please try again.";

const formatAge = (age?: string | number | null) => {
  if (age === null || age === undefined || age === "") return "";
  if (typeof age === "number") return String(age);

  const value = String(age).trim();
  const years = value.match(/(\d+)\s*y/i)?.[1];
  const months = value.match(/(\d+)\s*m/i)?.[1];

  if (years && years !== "0") return years;
  if (months) return `${months}m`;
  if (years) return years;

  return value;
};

const formatGender = (gender?: string | null) => {
  if (!gender) return "";
  const value = gender.trim().toLowerCase();
  if (value.startsWith("f")) return "F";
  if (value.startsWith("m")) return "M";
  return gender.trim().charAt(0).toUpperCase();
};

export default function UploadReportDialog({
  closeFn,
  patientId,
  isFromAppointment,
  appointmentId,
  patientName,
  patientAge,
  patientGender,
}: UploadDialogInterface) {
  const userProfile = utils.retrieveUserProfile();
  const profileUploadUrl =
    "/api/v1/users/profile/" + patientId + "/reports/" + userProfile.clinicId;
  const appointmentReportsUrl =
    "/api/v1/users/reports?appointmentId=" + appointmentId;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [show, setShow] = useState(true);
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedReports, setUploadedReports] = useState<
    { id: string; name: string; url?: string; fileFormat?: string }[]
  >([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [refreshReports, setRefreshReports] = useState(false);
  const [reportIdToDelete, setReportIdToDelete] = useState("");

  const { setSnackbar } = useSnackbarContext();

  useEffect(() => {
    if (!isFromAppointment) return;

    let active = true;
    setReportsLoading(true);
    HttpClient.performGet(appointmentReportsUrl)
      .then((response: HttpGetOutput<any>) => {
        if (!active) return;
        if (!response?.body?.status?.success) {
          setSnackbar({
            message: response?.body?.status?.message,
            severity: "error",
          });
          return;
        }
        setUploadedReports(response?.body?.reports ?? []);
      })
      .catch((e) => {
        if (!active) return;
        setSnackbar({
          message: "Error occurred while loading reports. " + e.message,
          severity: "error",
        });
      })
      .finally(() => {
        if (active) setReportsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isFromAppointment, appointmentReportsUrl, refreshReports, setSnackbar]);

  const clearPreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setShow(false);
    closeFn();
  };

  const resetFileInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const selectFile = (nextFile: File | undefined) => {
    if (!nextFile) return;

    if (!allowedFileTypes.includes(nextFile.type)) {
      setSnackbar({
        message: invalid_report_err_msg,
        severity: "error",
      });
      resetFileInputs();
      return;
    }

    clearPreview();
    const url = URL.createObjectURL(nextFile);
    previewUrlRef.current = url;
    setPreviewUrl(url);
    setFile(nextFile);
    resetFileInputs();
  };

  const removeFile = () => {
    clearPreview();
    setFile(null);
    resetFileInputs();
  };

  const showError = (message: string) => {
    setSnackbar({ message, severity: "error" });
  };

  const uploadReport = async () => {
    if (!file || !reportType || uploading) return;

    setUploading(true);
    try {
      const response: HttpMutationOutput<any> = await HttpClient.performPost(
        isFromAppointment ? appointmentReportsUrl : profileUploadUrl,
        {
          reportType: reportTypeForApi(reportType),
          note: "NA",
          name: file.name,
        },
      );

      if (!response?.body?.status?.success) {
        showError(response?.body?.status?.message);
        return;
      }

      const uploadUrl = response?.body?.file?.signedUrl;
      if (!uploadUrl) {
        showError("Upload url not found");
        return;
      }

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
          "Access-Control-Allow-Origin": "*",
          "Cross-Origin-Resource-Policy": "cross-origin",
        },
        body: file,
      });

      if (!putResponse?.ok) {
        showError("Failed to upload report");
        return;
      }

      setSnackbar({
        message: `${reportType} report uploaded`,
        severity: "success",
      });
      removeFile();
      setReportType(null);
      if (isFromAppointment) {
        setRefreshReports((value) => !value);
      }
    } catch (e) {
      showError(
        "Error occurred while uploading report. " + (e as Error).message,
      );
    } finally {
      setUploading(false);
    }
  };

  const ageLabel = formatAge(patientAge);
  const genderLabel = formatGender(patientGender);
  const ageGender = [ageLabel, genderLabel].filter(Boolean).join(" ");
  const subtitle = [
    patientName,
    ageGender,
    patientId ? String(patientId) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const isPdf = file?.type === "application/pdf";
  const canUpload = Boolean(file && reportType && !uploading);

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby="upload-report-title"
      PaperProps={{
        className: `upload-report-dialog${
          file ? " upload-report-dialog--with-preview" : ""
        }`,
      }}
    >
      <div className="upload-report-dialog__body">
        <div className="upload-report-dialog__form">
          <div className="upload-report-dialog__header">
            <div>
              <h2 id="upload-report-title" className="upload-report-dialog__title">
                Upload Report
              </h2>
              {subtitle && (
                <p className="upload-report-dialog__subtitle">{subtitle}</p>
              )}
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              className="upload-report-dialog__close"
            >
              <GrClose />
            </IconButton>
          </div>

          <div className="upload-report-dialog__section">
            <div className="upload-report-dialog__label">Report Type</div>
            <div className="upload-report-dialog__types">
              {REPORT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`upload-report-dialog__type${
                    reportType === type
                      ? " upload-report-dialog__type--selected"
                      : ""
                  }`}
                  onClick={() => setReportType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="upload-report-dialog__section">
            <div className="upload-report-dialog__label">File</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf, image/png, image/jpeg, image/jpg"
              hidden
              onChange={(e) => selectFile(e.target.files?.[0])}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              capture="environment"
              hidden
              onChange={(e) => selectFile(e.target.files?.[0])}
            />

            {file ? (
              <div className="upload-report-dialog__file">
                <FileTypeIcon pdf={isPdf} />
                <span className="upload-report-dialog__file-name" title={file.name}>
                  {file.name}
                </span>
                <button
                  type="button"
                  className="upload-report-dialog__remove"
                  aria-label="Remove file"
                  onClick={removeFile}
                >
                  <DeleteOutlinedIcon />
                </button>
              </div>
            ) : (
              <div className="upload-report-dialog__pickers">
                <button
                  type="button"
                  className="upload-report-dialog__picker"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload aria-hidden />
                  <span>Choose File</span>
                </button>
                <button
                  type="button"
                  className="upload-report-dialog__picker"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <FiCamera aria-hidden />
                  <span>Take Photo</span>
                </button>
              </div>
            )}
          </div>

          {isFromAppointment && (
            <div className="upload-report-dialog__section">
              <div className="upload-report-dialog__label">Uploaded Reports</div>
              {reportsLoading ? (
                <p className="upload-report-dialog__hint">Loading reports...</p>
              ) : uploadedReports.length === 0 ? (
                <p className="upload-report-dialog__hint">No reports uploaded</p>
              ) : (
                <div className="upload-report-dialog__existing">
                  {uploadedReports.map((report) => (
                    <div key={report.id} className="upload-report-dialog__file">
                      <FileTypeIcon pdf={!isImageFormat(report.fileFormat)} />
                      <a
                        className="upload-report-dialog__file-name"
                        href={report.url}
                        target="_blank"
                        rel="noreferrer"
                        title={report.name}
                      >
                        {report.name}
                      </a>
                      <button
                        type="button"
                        className="upload-report-dialog__remove"
                        aria-label="Delete report"
                        onClick={() => setReportIdToDelete(String(report.id))}
                      >
                        <DeleteOutlinedIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="upload-report-dialog__submit"
            disabled={!canUpload}
            onClick={uploadReport}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>

        {file && previewUrl && (
          <div className="upload-report-dialog__preview">
            <div className="upload-report-dialog__preview-label">Preview</div>
            <div className="upload-report-dialog__preview-frame">
              {isPdf ? (
                <iframe title="Report preview" src={previewUrl} />
              ) : (
                <img src={previewUrl} alt="Report preview" />
              )}
            </div>
          </div>
        )}
      </div>
      {reportIdToDelete && (
        <ConfirmDeleteReportDialog
          reportId={reportIdToDelete}
          onDailogCanceledFn={() => setReportIdToDelete("")}
          onReportDeletedFn={() => {
            setReportIdToDelete("");
            setRefreshReports((value) => !value);
          }}
        />
      )}
    </Dialog>
  );
}
